import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingsAPI, adminSettingsAPI } from '../services/api';
import { ArrowLeft, MapPin, Calendar, Moon, Loader, AlertCircle, CheckCircle, Clock, Star, X, User, Phone, Mail } from 'lucide-react';
import LocationSearchInputBackend from '../components/LocationSearchInputBackend';
import MapPicker from '../components/MapPicker';
import CombinedLocationSelector from '../components/CombinedLocationSelector';
import AnimatedClock from '../components/AnimatedClock';
import CancellationReasonModal from '../components/CancellationReasonModal';

// Define steps
const STEPS = {
  PICKUP: 1,
  DROPOFF: 2,
  BOOKING_DETAILS: 3,
  WAITING_FOR_DRIVER: 4,
};

interface LocationData {
  latitude: number;
  longitude: number;
  address: string;
}

const BookDriverRequestPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  
  // Multi-step state
  const [currentStep, setCurrentStep] = useState(STEPS.PICKUP);
  
  // Location states
  const [pickupSearchText, setPickupSearchText] = useState('');
  const [dropoffSearchText, setDropoffSearchText] = useState('');
  const [pickupLocation, setPickupLocation] = useState<LocationData | null>(null);
  const [dropoffLocation, setDropoffLocation] = useState<LocationData | null>(null);
  const [tempPickupLocation, setTempPickupLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [tempDropoffLocation, setTempDropoffLocation] = useState<{ lat: number; lng: number } | null>(null);
  
  // Booking details
  const [numberOfNights, setNumberOfNights] = useState(0);
  // Initialize with current date and time
  const getCurrentDateTime = () => {
    const now = new Date();
    // Round to nearest 15 minutes
    const minutes = Math.ceil(now.getMinutes() / 15) * 15;
    now.setMinutes(minutes);
    now.setSeconds(0);
    now.setMilliseconds(0);
    // Format as datetime-local string (YYYY-MM-DDTHH:mm)
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const mins = String(now.getMinutes()).padStart(2, '0');
    return {
      datetime: `${year}-${month}-${day}T${hours}:${mins}`,
      date: `${year}-${month}-${day}`,
      time: `${hours}:${mins}`
    };
  };
  
  const initialDateTime = getCurrentDateTime();
  const [bookingDate, setBookingDate] = useState(initialDateTime.date);
  const [startTime, setStartTime] = useState(initialDateTime.time);
  const [endTime, setEndTime] = useState('18:00');
  const [startDateTime, setStartDateTime] = useState(initialDateTime.datetime);
  const [endDateTime, setEndDateTime] = useState('');
  const [minEndDateTime, setMinEndDateTime] = useState('');

  // Calculate minimum end datetime based on start datetime and travel duration
  const calculateMinEndDateTime = (startDT: string, durationSeconds: number): string => {
    const startDate = new Date(startDT);
    const durationMinutes = Math.ceil(durationSeconds / 60) + 30; // Add 30 min buffer
    const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
    
    // Format as datetime-local string (YYYY-MM-DDTHH:mm)
    const year = endDate.getFullYear();
    const month = String(endDate.getMonth() + 1).padStart(2, '0');
    const day = String(endDate.getDate()).padStart(2, '0');
    const hours = String(endDate.getHours()).padStart(2, '0');
    const minutes = String(endDate.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  const [notes, setNotes] = useState('');
  const [estimatedPrice, setEstimatedPrice] = useState<any>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const [oneWayDistance, setOneWayDistance] = useState<number | null>(null); // Pickup-to-dropoff distance for threshold check
  const [agreedToTnC, setAgreedToTnC] = useState(false);
  const [tripType, setTripType] = useState<'roundTrip' | 'oneWay'>('roundTrip');
  const [oneWayAllowed, setOneWayAllowed] = useState(false);
  const [intermediateDestinations, setIntermediateDestinations] = useState<Array<{
    latitude: number;
    longitude: number;
    address: string;
    order: number;
  }>>([]);
  const [addingIntermediateStop, setAddingIntermediateStop] = useState(false);
  const [maxDestinations, setMaxDestinations] = useState(5);
  
  // Request tracking
  const [requestId, setRequestId] = useState<string | null>(null);
  const [waitingTimeLeft, setWaitingTimeLeft] = useState<number | null>(null);
  const [totalTimeout, setTotalTimeout] = useState<number | null>(null);
  const [requestStatus, setRequestStatus] = useState<'pending' | 'accepted' | 'expired' | 'cancelled' | null>(null);
  const [cancellationReason, setCancellationReason] = useState<string | null>(null);
  
  // Driver details modal
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [acceptedDriver, setAcceptedDriver] = useState<any>(null);
  
  // Cancellation modal
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  
  // Recent locations
  const [recentLocations, setRecentLocations] = useState<Array<{
    pickup: LocationData;
    dropoff: LocationData;
    intermediateDestinations?: Array<{
      latitude: number;
      longitude: number;
      address: string;
      order: number;
    }>;
    timestamp: number;
  }>>([]);
  const [hasManualSearch, setHasManualSearch] = useState(false);

  // Load recent locations from bookings API on mount
  useEffect(() => {
    loadRecentLocations();
    fetchAdminSettings();
  }, []);

  const [adminSettings, setAdminSettings] = useState<{ baseLocation?: { latitude: number; longitude: number }; oneWayTripThreshold?: number; maxDestinations?: number } | null>(null);

  const fetchAdminSettings = async () => {
    try {
      const response = await adminSettingsAPI.getSettings();
      if (response.success && response.data) {
        if (response.data.maxDestinations !== undefined) {
          setMaxDestinations(response.data.maxDestinations);
        }
        setAdminSettings({
          baseLocation: response.data.baseLocation,
          oneWayTripThreshold: response.data.oneWayTripThreshold,
          maxDestinations: response.data.maxDestinations
        });
      }
    } catch (error) {
      console.error('Error fetching admin settings:', error);
    }
  };

  // Calculate distance between two points using Haversine formula (for one-way trip check)
  const calculateDistanceBetweenPoints = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Radius of the Earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in km
  };

  const loadRecentLocations = async () => {
    try {
      const bookingsResponse = await bookingsAPI.getMyBookings({ limit: 100 });
      const bookings = bookingsResponse?.data || [];
      
      if (!Array.isArray(bookings)) {
        setRecentLocations([]);
        return;
      }
      
      // Filter driver bookings (both regular bookings and driver requests)
      const driverBookings = bookings.filter((booking: any) => 
        booking?.serviceType === 'driver' && 
        booking?.pickupLocation && 
        booking?.dropoffLocation &&
        booking.pickupLocation.latitude != null &&
        booking.pickupLocation.longitude != null &&
        booking.dropoffLocation.latitude != null &&
        booking.dropoffLocation.longitude != null
      );

      if (driverBookings.length === 0) {
        setRecentLocations([]);
        return;
      }

      // Sort by createdAt descending
      const sorted = driverBookings.sort((a: any, b: any) => {
        const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });

      // Get the 2 most recent bookings
      const recentBookings = sorted.slice(0, 2);
      if (recentBookings.length === 0) {
        setRecentLocations([]);
        return;
      }

      // Convert to recent location format
      const recentLocations = recentBookings
        .filter((booking: any) => 
          booking?.pickupLocation && 
          booking?.dropoffLocation &&
          booking.pickupLocation.latitude != null &&
          booking.pickupLocation.longitude != null &&
          booking.dropoffLocation.latitude != null &&
          booking.dropoffLocation.longitude != null
        )
        .map((booking: any) => {
          // Process intermediate destinations
          let intermediateDestinations: Array<{
            latitude: number;
            longitude: number;
            address: string;
            order: number;
          }> | undefined = undefined;
          
          if (booking.intermediateDestinations && Array.isArray(booking.intermediateDestinations)) {
            const validStops = booking.intermediateDestinations
              .filter((stop: any) => 
                stop && 
                typeof stop.latitude === 'number' && 
                typeof stop.longitude === 'number' &&
                !isNaN(stop.latitude) &&
                !isNaN(stop.longitude)
              )
              .map((stop: any, index: number) => ({
                latitude: stop.latitude,
                longitude: stop.longitude,
                address: stop.address || `Intermediate stop ${index + 1}`,
                order: typeof stop.order === 'number' ? stop.order : index + 1
              }));
            
            if (validStops.length > 0) {
              intermediateDestinations = validStops.sort((a: { order: number }, b: { order: number }) => a.order - b.order);
            }
          }
          
          return {
            pickup: {
              latitude: booking.pickupLocation.latitude,
              longitude: booking.pickupLocation.longitude,
              address: booking.pickupLocation.address || 'Pickup location'
            },
            dropoff: {
              latitude: booking.dropoffLocation.latitude,
              longitude: booking.dropoffLocation.longitude,
              address: booking.dropoffLocation.address || 'Dropoff location'
            },
            intermediateDestinations,
            timestamp: booking?.createdAt ? new Date(booking.createdAt).getTime() : Date.now()
          };
        });

      setRecentLocations(recentLocations);
    } catch (error) {
      console.error('Error loading recent locations:', error);
      setRecentLocations([]);
    }
  };

  // Calculate distance when both locations are set
  useEffect(() => {
    if (pickupLocation && dropoffLocation) {
      calculateDistance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupLocation, dropoffLocation, intermediateDestinations]);

  // Calculate number of nights from startDateTime and endDateTime
  const calculateNumberOfNights = (start: string, end: string): number => {
    if (!start || !end) return 0;
    
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      
      // Get date portions (YYYY-MM-DD) by setting time to midnight
      const startDateOnly = new Date(startDate);
      startDateOnly.setHours(0, 0, 0, 0);
      
      const endDateOnly = new Date(endDate);
      endDateOnly.setHours(0, 0, 0, 0);
      
      // Calculate difference in days
      const diffTime = endDateOnly.getTime() - startDateOnly.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // If end date is on a later day, that's the number of nights
      // e.g., Start: Day 1, End: Day 2 = 1 night
      // e.g., Start: Day 1, End: Day 3 = 2 nights
      return Math.max(0, diffDays);
    } catch (error) {
      console.error('Error calculating nights:', error);
      return 0;
    }
  };

  // Update end datetime when start datetime or distance changes
  useEffect(() => {
    if (startDateTime && distance !== null && distance > 0) {
      // Estimate duration based on distance (assume 40 km/h average)
      const estimatedDurationSeconds = (distance / 40) * 3600;
      const minEnd = calculateMinEndDateTime(startDateTime, estimatedDurationSeconds);
      setMinEndDateTime(minEnd);
      // Always update end datetime to minimum if it's not set or is before minimum
      if (!endDateTime || endDateTime < minEnd) {
        setEndDateTime(minEnd);
        // Also update endTime for backward compatibility
        const timeOnly = minEnd.split('T')[1];
        setEndTime(timeOnly);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateTime, distance]);

  // Auto-calculate numberOfNights when startDateTime or endDateTime changes
  useEffect(() => {
    if (startDateTime && endDateTime) {
      const nights = calculateNumberOfNights(startDateTime, endDateTime);
      setNumberOfNights(nights);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateTime, endDateTime]);

  // Calculate price when distance and nights are set
  // Note: For multi-destination trips, calculateDistance() already calls calculatePrice(),
  // so we only need to trigger it for single-destination trips or when numberOfNights changes
  useEffect(() => {
    // Only auto-calculate price for single-destination trips
    // Multi-destination trips are handled by calculateDistance() which calls calculatePrice()
    if (distance !== null && distance > 0 && intermediateDestinations.length === 0 && pickupLocation && dropoffLocation) {
      calculatePrice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distance, numberOfNights, intermediateDestinations.length]);

  // Also calculate price when booking details step is reached (in case distance was calculated but price wasn't)
  // Always recalculate when reaching booking details to ensure price is shown
  useEffect(() => {
    if (currentStep === STEPS.BOOKING_DETAILS && distance !== null && distance > 0 && pickupLocation && dropoffLocation) {
      console.log('calculatePrice - Triggering price calculation on booking details step', {
        distance,
        numberOfNights,
        hasEstimatedPrice: !!estimatedPrice
      });
      calculatePrice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, distance, pickupLocation, dropoffLocation]);

  // Initialize dropoff location fields when navigating to step 2 with existing dropoff location
  useEffect(() => {
    if (currentStep === STEPS.DROPOFF && dropoffLocation && !tempDropoffLocation) {
      setTempDropoffLocation({
        lat: dropoffLocation.latitude,
        lng: dropoffLocation.longitude
      });
      if (!dropoffSearchText) {
        setDropoffSearchText(dropoffLocation.address);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, dropoffLocation]);

  // Poll for request status when waiting for driver
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (currentStep === STEPS.WAITING_FOR_DRIVER && requestId) {
      interval = setInterval(() => {
        checkRequestStatus();
      }, 2000); // Poll every 2 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, requestId]);

  const calculateDistance = async () => {
    if (!pickupLocation || !dropoffLocation) return;

    // If multi-destination, use backend API to calculate total distance
    if (intermediateDestinations.length > 0) {
      try {
        // For multi-destination, calculatePrice() will handle both distance and price calculation
        // We just need to trigger it here, and it will set the distance correctly
        // But we still need to estimate duration, so we'll call the API here too
        // Determine effective trip type based on selection and threshold
        // For roundTrip, always use roundTrip
        // For oneWay, only use it if allowed, otherwise default to roundTrip
        const effectiveTripType = tripType === 'roundTrip' ? 'roundTrip' : (oneWayAllowed ? 'oneWay' : 'roundTrip');
        const response = await adminSettingsAPI.calculateMultiDestinationPrice({
          origin: { latitude: pickupLocation.latitude, longitude: pickupLocation.longitude },
          intermediateDestinations: intermediateDestinations.map(s => ({
            latitude: s.latitude,
            longitude: s.longitude
          })),
          finalDestination: { latitude: dropoffLocation.latitude, longitude: dropoffLocation.longitude },
          numberOfNights: numberOfNights,
          isTwoWay: effectiveTripType === 'roundTrip' // Round trip: A->B->C->A, One-way: A->B->C
        });
        console.log('calculateDistance: Multi-destination API response:', response);
        if (response && response.success && response.data) {
        const totalDistance = response.data.distance;
        console.log('calculateDistance: Multi-destination total distance:', totalDistance, 'km', effectiveTripType === 'roundTrip' ? '(with return journey)' : '(one-way)');
        
        // Set distance immediately so it's available for duration calculation
        setDistance(totalDistance);
        
        // Estimate duration and calculate minimum end datetime
        if (startDateTime) {
          const estimatedDurationSeconds = (totalDistance / 40) * 3600;
          const minEnd = calculateMinEndDateTime(startDateTime, estimatedDurationSeconds);
          setMinEndDateTime(minEnd);
          if (!endDateTime || endDateTime < minEnd) {
            setEndDateTime(minEnd);
            const timeOnly = minEnd.split('T')[1];
            setEndTime(timeOnly);
          }
        }
        
        // calculatePrice() will also call the same API and update both price and distance
        // This ensures consistency - both will have the same distance value
          // But we already have the price data in the response, so we can set it directly
          if (response.data.total !== undefined) {
            setEstimatedPrice(response.data);
            console.log('calculateDistance: Price set from distance calculation response');
          } else {
            // If price not in response, call calculatePrice
        calculatePrice();
          }
        } else {
          console.error('calculateDistance: Invalid API response:', response);
          setError('Failed to calculate distance. Please try again.');
        }
        return;
      } catch (error) {
        console.error('Error calculating multi-destination distance:', error);
        // Fallback to single destination calculation
      }
    }

    // Single destination - use Google Maps Distance Matrix API if available
    if (typeof google !== 'undefined' && google.maps && google.maps.DistanceMatrixService) {
      const service = new google.maps.DistanceMatrixService();
      
      service.getDistanceMatrix(
        {
          origins: [{ lat: pickupLocation.latitude, lng: pickupLocation.longitude }],
          destinations: [{ lat: dropoffLocation.latitude, lng: dropoffLocation.longitude }],
          travelMode: google.maps.TravelMode.DRIVING,
          unitSystem: google.maps.UnitSystem.METRIC,
        },
        (response, status) => {
          if (status === 'OK' && response) {
            const distanceInMeters = response.rows[0].elements[0].distance?.value ?? 0;
            const distanceInKm = distanceInMeters / 1000;
            const durationInSeconds = response.rows[0].elements[0].duration?.value ?? 0;
            
            setDistance(distanceInKm);
            setOneWayDistance(distanceInKm); // Store pickup-to-dropoff distance for threshold check
            
            // Check if one-way trip is allowed (distance < threshold)
            if (adminSettings?.oneWayTripThreshold) {
              const allowed = distanceInKm < adminSettings.oneWayTripThreshold;
              setOneWayAllowed(allowed);
            }
            
            // Calculate minimum end datetime based on travel duration
            if (startDateTime && durationInSeconds) {
              const minEnd = calculateMinEndDateTime(startDateTime, durationInSeconds);
              setMinEndDateTime(minEnd);
              if (!endDateTime || endDateTime < minEnd) {
                setEndDateTime(minEnd);
                const timeOnly = minEnd.split('T')[1];
                setEndTime(timeOnly);
              }
            } else if (startDateTime) {
              // Fallback: estimate from distance if duration not available
              const estimatedDurationSeconds = (distanceInKm / 40) * 3600;
              const minEnd = calculateMinEndDateTime(startDateTime, estimatedDurationSeconds);
              setMinEndDateTime(minEnd);
              if (!endDateTime || endDateTime < minEnd) {
                setEndDateTime(minEnd);
                const timeOnly = minEnd.split('T')[1];
                setEndTime(timeOnly);
              }
            }
            
            calculatePrice();
          } else {
            // Fallback to Haversine
            calculateDistanceHaversine();
          }
        }
      );
    } else {
      // Fallback to Haversine if Google Maps not available
      calculateDistanceHaversine();
    }
  };

  const calculateDistanceHaversine = () => {
    if (!pickupLocation || !dropoffLocation) return;
    
    const R = 6371; // Earth's radius in km
    const dLat = (dropoffLocation.latitude - pickupLocation.latitude) * Math.PI / 180;
    const dLon = (dropoffLocation.longitude - pickupLocation.longitude) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(pickupLocation.latitude * Math.PI / 180) * Math.cos(dropoffLocation.latitude * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const calculatedDistance = R * c;
    
    setDistance(calculatedDistance);
    setOneWayDistance(calculatedDistance); // Store pickup-to-dropoff distance for threshold check
    
    // Check if one-way trip is allowed (distance < threshold)
    if (adminSettings?.oneWayTripThreshold) {
      const allowed = calculatedDistance < adminSettings.oneWayTripThreshold;
      setOneWayAllowed(allowed);
    }
    
    // Estimate duration and calculate minimum end datetime
    if (startDateTime) {
      const estimatedDurationSeconds = (calculatedDistance / 40) * 3600;
      const minEnd = calculateMinEndDateTime(startDateTime, estimatedDurationSeconds);
      setMinEndDateTime(minEnd);
      if (!endDateTime || endDateTime < minEnd) {
        setEndDateTime(minEnd);
        const timeOnly = minEnd.split('T')[1];
        setEndTime(timeOnly);
      }
    }
    
    calculatePrice();
  };

  const calculatePrice = async (overrideTripType?: 'roundTrip' | 'oneWay') => {
    if (distance === null || !pickupLocation || !dropoffLocation) {
      console.log('calculatePrice - Skipping: distance=', distance, 'pickupLocation=', !!pickupLocation, 'dropoffLocation=', !!dropoffLocation);
      return;
    }
    
    setCalculatingPrice(true);
    try {
      // Use overrideTripType if provided, otherwise use current tripType state
      const currentTripType = overrideTripType ?? tripType;
      
      console.log('calculatePrice - Starting calculation', {
        distance,
        numberOfNights,
        hasIntermediateDestinations: intermediateDestinations.length > 0,
        tripType: currentTripType,
        overrideTripType
      });
      
      // Recalculate one-way threshold check if needed
      // Calculate pickup-to-dropoff distance for threshold check
      let distanceForThreshold = oneWayDistance;
      if (distanceForThreshold === null && pickupLocation && dropoffLocation) {
        // Calculate pickup-to-dropoff distance using Haversine formula
        distanceForThreshold = calculateDistanceBetweenPoints(
          pickupLocation.latitude,
          pickupLocation.longitude,
          dropoffLocation.latitude,
          dropoffLocation.longitude
        );
        setOneWayDistance(distanceForThreshold); // Store it for future use
      }
      
      // Check threshold if admin settings are available
      if (adminSettings?.oneWayTripThreshold && distanceForThreshold !== null) {
        const allowed = distanceForThreshold < adminSettings.oneWayTripThreshold;
        setOneWayAllowed(allowed);
      }
      
      // If multi-destination, use calculateMultiDestinationPrice
      // Multi-destination trips - use selected trip type
      // Round trip: A->B->C->A (includes return journey), One-way: A->B->C (no return)
      if (intermediateDestinations.length > 0) {
        // Use current tripType (with override if provided)
        // For roundTrip, always use roundTrip
        // For oneWay, only use it if allowed, otherwise default to roundTrip
        const effectiveTripType = currentTripType === 'roundTrip' ? 'roundTrip' : (oneWayAllowed ? 'oneWay' : 'roundTrip');
        const response = await adminSettingsAPI.calculateMultiDestinationPrice({
          origin: { latitude: pickupLocation.latitude, longitude: pickupLocation.longitude },
          intermediateDestinations: intermediateDestinations.map(s => ({
            latitude: s.latitude,
            longitude: s.longitude
          })),
          finalDestination: { latitude: dropoffLocation.latitude, longitude: dropoffLocation.longitude },
          numberOfNights: numberOfNights,
          isTwoWay: effectiveTripType === 'roundTrip' // Round trip includes return journey
        });
        
        console.log('calculatePrice - Multi-destination price response:', response);
        // API service returns { success: true, data: {...} }, so response.data is the pricing data
        if (response && response.success && response.data) {
          console.log('calculatePrice - Distance from response:', response.data.distance, 'km');
          setEstimatedPrice(response.data);
          // Update distance with the total distance including return journey
          // This should be the full round trip distance: A->B->C->D->A
          if (response.data.distance !== undefined && response.data.distance !== null) {
            console.log('calculatePrice - Setting distance state to:', response.data.distance, 'km (includes return journey)');
            setDistance(response.data.distance);
          } else {
            console.error('calculatePrice - No distance in response.data:', response.data);
          }
        } else {
          console.error('calculatePrice - Multi-destination API call failed or invalid response:', response);
          setError('Failed to calculate price. Please try again.');
        }
      } else {
        // Single destination - check if out of range and needs round trip pricing
        let needsRoundTrip = false;
        let returnDistance = 0;
        
        if (adminSettings?.baseLocation && adminSettings?.oneWayTripThreshold) {
          const finalToBaseDistance = calculateDistanceBetweenPoints(
            dropoffLocation.latitude,
            dropoffLocation.longitude,
            adminSettings.baseLocation.latitude,
            adminSettings.baseLocation.longitude
          );
          
          // If final dropoff is NOT within threshold, add return journey distance
          if (finalToBaseDistance >= adminSettings.oneWayTripThreshold) {
            needsRoundTrip = true;
            returnDistance = calculateDistanceBetweenPoints(
              dropoffLocation.latitude,
              dropoffLocation.longitude,
              pickupLocation.latitude,
              pickupLocation.longitude
            );
          }
        }
        
        // Single destination - if out of range, calculate round trip (include return journey)
        if (needsRoundTrip && returnDistance > 0) {
          const roundTripDistance = distance + returnDistance;
          console.log('calculatePrice - Calculating round trip price for distance:', roundTripDistance, 'km');
          const response = await adminSettingsAPI.calculateDriverPrice({
            distance: roundTripDistance,
            numberOfNights,
            tripType: 'roundTrip'
          });
          console.log('calculatePrice - Round trip price response:', response);
          if (response && response.success && response.data) {
            setEstimatedPrice(response.data);
            setDistance(roundTripDistance);
            console.log('💰 Round trip price calculated and set:', response.data);
          } else {
            console.error('calculatePrice - Round trip API call failed or invalid response:', response);
            setError('Failed to calculate price. Please try again.');
          }
        } else {
          // Use current tripType (with override if provided)
          // For roundTrip, always use roundTrip
          // For oneWay, only use it if allowed, otherwise default to roundTrip
          const effectiveTripType = currentTripType === 'roundTrip' ? 'roundTrip' : (oneWayAllowed ? 'oneWay' : 'roundTrip');
          
          // Calculate distance for pricing based on trip type
          // For one-way: use only one-way distance (pickup-to-dropoff)
          // For round trip: calculate round trip distance (one-way + return)
          let distanceForPricing: number;
          if (effectiveTripType === 'oneWay') {
            // One-way: use only the one-way distance
            distanceForPricing = oneWayDistance ?? distance ?? 0;
          } else {
            // Round trip: calculate total distance (one-way + return)
            if (!pickupLocation || !dropoffLocation) {
              console.error('calculatePrice - Missing pickup or dropoff location for round trip calculation');
              return;
            }
            const oneWayDist = oneWayDistance ?? distance ?? 0;
            const returnDist = calculateDistanceBetweenPoints(
              dropoffLocation.latitude,
              dropoffLocation.longitude,
              pickupLocation.latitude,
              pickupLocation.longitude
            );
            distanceForPricing = oneWayDist + returnDist;
            // Update distance state to reflect round trip distance
            setDistance(distanceForPricing);
          }
          
          console.log('calculatePrice - Calculating single destination price for distance:', distanceForPricing, 'km, nights:', numberOfNights, 'tripType:', effectiveTripType);
          const response = await adminSettingsAPI.calculateDriverPrice({
            distance: distanceForPricing,
            numberOfNights,
            tripType: effectiveTripType
          });
          console.log('calculatePrice - Single destination price response:', response);
          if (response && response.success && response.data) {
            setEstimatedPrice(response.data);
            // Update distance from response if available
            if (response.data.distance !== undefined && response.data.distance !== null) {
              setDistance(response.data.distance);
            }
            console.log('💰 Price calculated and set:', response.data);
          } else {
            console.error('calculatePrice - Single destination API call failed or invalid response:', response);
            setError('Failed to calculate price. Please try again.');
          }
        }
      }
    } catch (err: any) {
      console.error('Error calculating price:', err);
      setError(err.response?.data?.message || 'Failed to calculate price. Please try again.');
    } finally {
      setCalculatingPrice(false);
    }
  };

  const checkRequestStatus = async () => {
    if (!requestId) return;
    
    try {
      const response = await bookingsAPI.getDriverRequestStatus(requestId);
      const { status, expiresAt, booking, cancellationReason } = response.data;
      
      setRequestStatus(status);
      
      if (status === 'cancelled') {
        setCancellationReason(cancellationReason ?? null);
      }
      
      if (status === 'accepted' && booking) {
        // Driver accepted! Show driver details modal
        setAcceptedDriver(booking.provider || booking.providerId);
        setShowDriverModal(true);
      } else if (status === 'expired') {
        // Request expired
        setError('No drivers accepted your request within the time limit. Please try again.');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else if (expiresAt) {
        // Calculate time left
        const timeLeft = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
        setWaitingTimeLeft(timeLeft);
        // Update total timeout if not set
        if (!totalTimeout) {
          const total = timeLeft > 0 ? timeLeft : 300; // Default to 5 minutes
          setTotalTimeout(total);
        }
      }
    } catch (err: any) {
      console.error('Error checking request status:', err);
    }
  };

  const handleCloseDriverModal = () => {
    setShowDriverModal(false);
    navigate('/bookings');
  };

  const selectRecentLocation = (location: { 
    pickup: LocationData; 
    dropoff: LocationData; 
    intermediateDestinations?: Array<{
      latitude: number;
      longitude: number;
      address: string;
      order: number;
    }>;
  }) => {
    setPickupLocation(location.pickup);
    setDropoffLocation(location.dropoff);
    setPickupSearchText(location.pickup.address);
    setDropoffSearchText(location.dropoff.address);
    // Set intermediate destinations if they exist
    if (location.intermediateDestinations && location.intermediateDestinations.length > 0) {
      setIntermediateDestinations(location.intermediateDestinations);
    } else {
      setIntermediateDestinations([]);
    }
    // Reset distance and price to force recalculation
    setDistance(null);
    setEstimatedPrice(null);
    // Skip directly to booking details
    setCurrentStep(STEPS.BOOKING_DETAILS);
  };

  const handlePickupPlaceSelected = (value: string, placeDetails?: any) => {
    setHasManualSearch(true);
    // Extract location from placeDetails (handles both geometry.location and direct location formats)
    let lat: number | undefined;
    let lng: number | undefined;
    
    if (placeDetails?.geometry?.location) {
      // Handle Google Places API format with functions
      lat = typeof placeDetails.geometry.location.lat === 'function' 
        ? placeDetails.geometry.location.lat() 
        : placeDetails.geometry.location.lat;
      lng = typeof placeDetails.geometry.location.lng === 'function' 
        ? placeDetails.geometry.location.lng() 
        : placeDetails.geometry.location.lng;
    } else if (placeDetails?.location) {
      // Handle direct location format
      lat = typeof placeDetails.location.lat === 'function' 
        ? placeDetails.location.lat() 
        : placeDetails.location.lat;
      lng = typeof placeDetails.location.lng === 'function' 
        ? placeDetails.location.lng() 
        : placeDetails.location.lng;
    }
    
    if (lat !== undefined && lng !== undefined) {
      setTempPickupLocation({
        lat,
        lng,
      });
    }
  };

  const handlePickupLocationSelect = (location: { lat: number; lng: number; address: string }) => {
    setHasManualSearch(true);
    setTempPickupLocation({ lat: location.lat, lng: location.lng });
    setPickupSearchText(location.address);
  };

  const confirmPickupLocation = async () => {
    if (!tempPickupLocation || !pickupSearchText) {
      return;
    }

    // Check if location is within coverage area
    try {
      setLoading(true);
      setError('');
      const response = await bookingsAPI.checkPickupLocation(
        tempPickupLocation.lat,
        tempPickupLocation.lng
      );

      if (!response.data.isWithinCoverage) {
        setError(response.data.message || 'Pickup location is outside our service coverage area. Please select a location within our coverage area.');
        setLoading(false);
        return;
      }
    
      // Location is within coverage - proceed
      setHasManualSearch(true);
      setPickupLocation({
        latitude: tempPickupLocation.lat,
        longitude: tempPickupLocation.lng,
        address: pickupSearchText,
      });
      
      setCurrentStep(STEPS.DROPOFF);
    } catch (error: any) {
      console.error('Error checking pickup location:', error);
      // If check fails, show error and don't proceed (fail closed for security)
      setError(error.response?.data?.message || 'Failed to verify pickup location. Please try again or select a different location.');
    } finally {
      setLoading(false);
    }
  };

  const handleDropoffPlaceSelected = (value: string, placeDetails?: any) => {
    // Extract location from placeDetails (handles both geometry.location and direct location formats)
    let lat: number | undefined;
    let lng: number | undefined;
    
    if (placeDetails?.geometry?.location) {
      // Handle Google Places API format with functions
      lat = typeof placeDetails.geometry.location.lat === 'function' 
        ? placeDetails.geometry.location.lat() 
        : placeDetails.geometry.location.lat;
      lng = typeof placeDetails.geometry.location.lng === 'function' 
        ? placeDetails.geometry.location.lng() 
        : placeDetails.geometry.location.lng;
    } else if (placeDetails?.location) {
      // Handle direct location format
      lat = typeof placeDetails.location.lat === 'function' 
        ? placeDetails.location.lat() 
        : placeDetails.location.lat;
      lng = typeof placeDetails.location.lng === 'function' 
        ? placeDetails.location.lng() 
        : placeDetails.location.lng;
    }
    
    if (lat !== undefined && lng !== undefined) {
      setTempDropoffLocation({
        lat,
        lng,
      });
    }
  };

  const handleDropoffLocationSelect = (location: { lat: number; lng: number; address: string }) => {
    setTempDropoffLocation({ lat: location.lat, lng: location.lng });
    setDropoffSearchText(location.address);
  };

  const confirmDropoffLocation = () => {
    if (!tempDropoffLocation || !dropoffSearchText) {
      return;
    }
    
    if (addingIntermediateStop) {
      // Add as intermediate stop
      if (intermediateDestinations.length >= maxDestinations) {
        alert(`Maximum ${maxDestinations} intermediate destinations allowed`);
        return;
      }
      const newStop = {
        latitude: tempDropoffLocation.lat,
        longitude: tempDropoffLocation.lng,
        address: dropoffSearchText,
        order: intermediateDestinations.length + 1
      };
      setIntermediateDestinations([...intermediateDestinations, newStop]);
      setDropoffSearchText('');
      setTempDropoffLocation(null);
      setAddingIntermediateStop(false);
      // Stay on dropoff step to allow adding more stops or final destination
    } else {
      // Set as final dropoff location
      const finalDropoff = {
        latitude: tempDropoffLocation.lat,
        longitude: tempDropoffLocation.lng,
        address: dropoffSearchText,
      };

      // Check one-way trip eligibility: if final dropoff is NOT within threshold from base,
      // inform user that round trip will be automatically used (return journey will be added)
      if (intermediateDestinations.length > 0 && adminSettings?.baseLocation && adminSettings?.oneWayTripThreshold && pickupLocation) {
        const finalToBaseDistance = calculateDistanceBetweenPoints(
          finalDropoff.latitude,
          finalDropoff.longitude,
          adminSettings.baseLocation.latitude,
          adminSettings.baseLocation.longitude
        );
        
        // If final dropoff is NOT within threshold, round trip will be used automatically
        // The backend will add return journey from final dropoff back to pickup
        if (finalToBaseDistance >= adminSettings.oneWayTripThreshold) {
          alert(`Final dropoff is ${finalToBaseDistance.toFixed(1)}km from base location (threshold: ${adminSettings.oneWayTripThreshold}km). Round trip will be automatically used - return journey from final destination back to pickup will be included.`);
        }
      }

      // Always keep the original dropoff location - backend will handle round trip calculation
      setDropoffLocation(finalDropoff);
      setCurrentStep(STEPS.BOOKING_DETAILS);
    }
  };

  const handleCurrentLocationForPickup = (skipToDropoff = false) => {
    setHasManualSearch(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          // Reverse geocode to get address
          try {
            const geocoder = new google.maps.Geocoder();
            const result = await geocoder.geocode({ location: { lat, lng } });
            
            const address = result.results[0]?.formatted_address || 'Current Location';
            
            setPickupLocation({
              latitude: lat,
              longitude: lng,
              address: address,
            });
            setPickupSearchText(address);
            setTempPickupLocation({ lat, lng });
            
            if (skipToDropoff) {
              setCurrentStep(STEPS.DROPOFF);
            } else {
              setCurrentStep(STEPS.PICKUP);
            }
          } catch (error) {
            console.error('Geocoding error:', error);
            // Still proceed with coordinates
            setPickupLocation({
              latitude: lat,
              longitude: lng,
              address: 'Current Location',
            });
            setPickupSearchText('Current Location');
            setTempPickupLocation({ lat, lng });
            
            if (skipToDropoff) {
              setCurrentStep(STEPS.DROPOFF);
            } else {
              setCurrentStep(STEPS.PICKUP);
            }
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Could not get your current location. Please enable location permissions.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      alert('Geolocation is not supported by your browser');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!pickupLocation || !dropoffLocation) {
      setError('Both pickup and dropoff locations are required');
      return;
    }

    if (!bookingDate) {
      setError('Booking date is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Extract bookingDate and timeSlot from datetime inputs
      if (!startDateTime || !endDateTime) {
        setError('Start and end date/time are required');
        return;
      }

      // Validate that booking date/time is in the future
      const bookingDateTime = new Date(startDateTime);
      const now = new Date();
      if (bookingDateTime <= now) {
        setError('Please select a date and time in the future. Past bookings are not allowed.');
        setLoading(false);
        return;
      }

      // Validate end datetime is not before minimum
      if (minEndDateTime && endDateTime < minEndDateTime) {
        setError(`End datetime cannot be earlier than the estimated minimum time. Please set a later time.`);
        setLoading(false);
        return;
      }

      if (!agreedToTnC) {
        setError('Please agree to the Terms and Conditions and Privacy Policy to proceed');
        return;
      }

      // Convert to ISO strings for tripStartDateTime and tripEndDateTime
      const tripStartISO = new Date(startDateTime).toISOString();
      const tripEndISO = new Date(endDateTime).toISOString();
      
      const response = await bookingsAPI.createDriverRequest({
        tripStartDateTime: tripStartISO, // Full ISO datetime string
        tripEndDateTime: tripEndISO, // Full ISO datetime string
        notes,
        pickupLocation,
        dropoffLocation,
        numberOfNights,
        intermediateDestinations: intermediateDestinations.length > 0 ? intermediateDestinations : undefined,
      });
      
      setRequestId(response.data.requestId);
      const expiresAt = new Date(response.data.expiresAt);
      const nowTimestamp = Date.now();
      const timeLeft = Math.max(0, Math.floor((expiresAt.getTime() - nowTimestamp) / 1000));
      const total = (response.data.timeoutMinutes ?? 5) * 60;
      setWaitingTimeLeft(timeLeft);
      setTotalTimeout(total);
      setCurrentStep(STEPS.WAITING_FOR_DRIVER);
    } catch (err: any) {
      console.error('Request error:', err);
      setError(err.response?.data?.message || 'Request failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case STEPS.PICKUP:
        return (
          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            <CombinedLocationSelector
              value={pickupSearchText}
              onChange={handlePickupPlaceSelected}
              onCurrentLocationClick={() => handleCurrentLocationForPickup(false)}
              placeholder="Search pickup location (e.g., Connaught Place, Delhi)"
              initialLocation={tempPickupLocation}
              onLocationSelect={handlePickupLocationSelect}
              onConfirm={confirmPickupLocation}
              confirmButtonText="Confirm Pickup"
              stepTitle="Step 1: Select Pickup Location"
              address={pickupLocation?.address}
              middleContent={
                !hasManualSearch && recentLocations.length > 0 ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h5 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      Recent Trips - Click to use
                    </h5>
                    <div className="space-y-2">
                      {recentLocations.map((location, index) => (
                        <button
                          key={index}
                          onClick={() => selectRecentLocation(location)}
                          className="w-full text-left p-3 bg-white rounded-md hover:bg-white transition-colors border border-gray-700"
                        >
                          <div className="flex items-start text-xs">
                            <MapPin className="h-3 w-3 text-green-600 mr-1 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700 line-clamp-1">{location.pickup.address}</span>
                          </div>
                          {location.intermediateDestinations && Array.isArray(location.intermediateDestinations) && location.intermediateDestinations.length > 0 && (
                            <>
                              {location.intermediateDestinations
                                .sort((a, b) => (a.order || 0) - (b.order || 0))
                                .map((stop, stopIndex) => (
                                  <div key={`stop-${stopIndex}-${stop.order || stopIndex}`} className="flex items-start text-xs mt-1">
                                    <MapPin className="h-3 w-3 text-blue-600 mr-1 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-700 line-clamp-1">Stop {stop.order || stopIndex + 1}: {stop.address}</span>
                                  </div>
                                ))}
                            </>
                          )}
                          <div className="flex items-start text-xs mt-1">
                            <MapPin className="h-3 w-3 text-red-600 mr-1 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700 line-clamp-1">{location.dropoff.address}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-blue-700 mt-2">
                      Clicking a recent trip will auto-fill locations and skip to booking details
                    </p>
                  </div>
                ) : !hasManualSearch ? (
                  <div className="bg-white border border-gray-800 rounded-lg p-3 mb-4">
                    <p className="text-xs text-gray-300 flex items-center">
                      <Clock className="h-3 w-3 mr-2 flex-shrink-0" />
                      💡 Tip: After your first booking, recent trips will appear here for quick rebooking!
                    </p>
                  </div>
                ) : null
              }
            />
          </div>
        );

      case STEPS.DROPOFF:
        return (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
              <p className="text-sm text-green-800">
                ✓ Pickup location confirmed: {pickupLocation?.address}
              </p>
            </div>

            {/* Intermediate Destinations List */}
            {intermediateDestinations.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                <h5 className="text-sm font-semibold text-blue-900 mb-2">Intermediate Stops ({intermediateDestinations.length}/{maxDestinations})</h5>
                <div className="space-y-2">
                  {intermediateDestinations.map((stop, index) => (
                    <div key={index} className="flex items-center justify-between bg-white rounded-md p-2">
                      <div className="flex items-center flex-1">
                        <span className="text-xs font-semibold text-blue-600 mr-2">Stop {stop.order}</span>
                        <span className="text-xs text-gray-700 truncate">{stop.address}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = intermediateDestinations.filter((_, i) => i !== index);
                          // Reorder remaining stops
                          const reordered = updated.map((s, i) => ({ ...s, order: i + 1 }));
                          setIntermediateDestinations(reordered);
                        }}
                        className="text-red-600 hover:text-red-800 ml-2"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Intermediate Stop Button - Above Confirm Dropoff */}
            {!addingIntermediateStop && intermediateDestinations.length < maxDestinations && (
              <button
                type="button"
                onClick={() => {
                  setAddingIntermediateStop(true);
                  setDropoffSearchText('');
                  setTempDropoffLocation(null);
                }}
                className="w-full px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
              >
                + Add Intermediate Stop ({intermediateDestinations.length}/{maxDestinations})
              </button>
            )}

            {/* Show dropoff confirmation if already set, but still allow editing */}
            {dropoffLocation && !addingIntermediateStop && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
                <p className="text-sm text-green-800">
                  ✓ Dropoff location confirmed: {dropoffLocation.address}
                </p>
                <p className="text-xs text-green-700 mt-1">
                  You can edit the dropoff location below if needed
                </p>
              </div>
            )}

            {/* Always show location selector to allow editing */}
            {!addingIntermediateStop ? (
              <CombinedLocationSelector
                value={dropoffSearchText || dropoffLocation?.address || ''}
                onChange={handleDropoffPlaceSelected}
                placeholder="Search dropoff location (e.g., India Gate, Delhi)"
                initialLocation={tempDropoffLocation || (dropoffLocation ? { lat: dropoffLocation.latitude, lng: dropoffLocation.longitude } : null)}
                onLocationSelect={handleDropoffLocationSelect}
                onConfirm={confirmDropoffLocation}
                confirmButtonText={dropoffLocation ? 'Update Dropoff Location' : 'Confirm Dropoff'}
                stepTitle="Step 2: Select Dropoff Location"
                address={dropoffLocation?.address}
              />
            ) : (
              <>
                <CombinedLocationSelector
                  value={dropoffSearchText}
                  onChange={handleDropoffPlaceSelected}
                  placeholder={`Search intermediate stop ${intermediateDestinations.length + 1} location`}
                  initialLocation={tempDropoffLocation}
                  onLocationSelect={handleDropoffLocationSelect}
                  onConfirm={confirmDropoffLocation}
                  confirmButtonText={`Add Stop ${intermediateDestinations.length + 1}`}
                  stepTitle={`Step 2: Add Intermediate Stop ${intermediateDestinations.length + 1}`}
                  address={undefined}
                />

                {/* Cancel Adding Stop Button */}
                <button
                  type="button"
                  onClick={() => {
                    setAddingIntermediateStop(false);
                    setDropoffSearchText('');
                    setTempDropoffLocation(null);
                  }}
                  className="w-full px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                >
                  Cancel Adding Stop
                </button>
              </>
            )}

            {/* Show continue button when dropoff is confirmed and not adding intermediate stop */}
            {dropoffLocation && !addingIntermediateStop && (
              <button
                type="button"
                onClick={() => {
                  setCurrentStep(STEPS.BOOKING_DETAILS);
                }}
                className="w-full px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
              >
                Continue to Booking Details
              </button>
            )}
          </div>
        );

      case STEPS.BOOKING_DETAILS:
        // Calculate if round trip is needed for multi-destination trips
        const needsRoundTrip = intermediateDestinations.length > 0 && 
          pickupLocation && 
          dropoffLocation && 
          adminSettings?.baseLocation && 
          adminSettings?.oneWayTripThreshold ? (() => {
            const finalToBaseDistance = calculateDistanceBetweenPoints(
              dropoffLocation.latitude,
              dropoffLocation.longitude,
              adminSettings.baseLocation.latitude,
              adminSettings.baseLocation.longitude
            );
            return finalToBaseDistance >= adminSettings.oneWayTripThreshold;
          })() : false;
        
        return (
          <form onSubmit={handleSubmit} className="space-y-6">
            <h4 className="text-lg font-semibold mb-4">Step 3: Booking Details</h4>
            
            {/* Location Summary */}
            <div className="bg-white rounded-lg p-4 space-y-2">
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Pickup</p>
                  <p className="text-sm text-gray-300">{pickupLocation?.address}</p>
                </div>
              </div>
              
              {/* Intermediate Destinations */}
              {intermediateDestinations.length > 0 && (
                <>
                  {intermediateDestinations.map((stop, index) => (
                    <div key={index} className="flex items-start">
                      <MapPin className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Stop {stop.order}</p>
                        <p className="text-sm text-gray-300">{stop.address}</p>
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Final Dropoff</p>
                  <p className="text-sm text-gray-300">{dropoffLocation?.address}</p>
                </div>
              </div>
              
              {/* Show return journey indicator for multi-destination trips when final dropoff is beyond threshold */}
              {needsRoundTrip && (
                <div className="flex items-start">
                  <MapPin className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Return to Pickup</p>
                    <p className="text-sm text-gray-300">{pickupLocation?.address}</p>
                    <p className="text-xs text-blue-600 mt-1">(Round trip - automatically included)</p>
                  </div>
                </div>
              )}
              
              {distance && (
                <div className="pt-2 border-t">
                  <p className="text-sm font-medium text-gray-700">Total Distance: {typeof distance === 'number' ? distance.toFixed(2) : (distance ?? '0.00')} km</p>
                  {intermediateDestinations.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">(Includes return journey from final destination to pickup)</p>
                  )}
                </div>
              )}
            </div>

            {/* Trip Type Selection - Show when distance is available */}
            {distance !== null && pickupLocation && dropoffLocation && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Trip Type:
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tripType"
                      value="roundTrip"
                      checked={tripType === 'roundTrip'}
                      onChange={async () => {
                        setTripType('roundTrip');
                        // Pass the new trip type directly to avoid state update timing issues
                        await calculatePrice('roundTrip');
                      }}
                      className="h-4 w-4 text-black focus:ring-gray-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Round Trip</span>
                  </label>
                  <label className={`flex items-center space-x-2 ${oneWayAllowed ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                    <input
                      type="radio"
                      name="tripType"
                      value="oneWay"
                      checked={tripType === 'oneWay'}
                      onChange={async () => {
                        if (oneWayAllowed) {
                          setTripType('oneWay');
                          // Pass the new trip type directly to avoid state update timing issues
                          await calculatePrice('oneWay');
                        }
                      }}
                      disabled={!oneWayAllowed}
                      className="h-4 w-4 text-black focus:ring-gray-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-700">One Way</span>
                  </label>
                </div>
                {!oneWayAllowed && (
                  <p className="text-xs text-gray-600 mt-2">
                    ⚠️ One-way trips are only available for distances less than the threshold.
                  </p>
                )}
                {oneWayAllowed && (
                  <p className="text-xs text-gray-600 mt-2">
                    {tripType === 'roundTrip' 
                      ? `Round trip includes return journey (Total: ${distance !== null && distance !== undefined ? distance.toFixed(2) : '0.00'} km)`
                      : `One-way trip (Distance: ${distance !== null && distance !== undefined ? distance.toFixed(2) : '0.00'} km)`}
                  </p>
                )}
              </div>
            )}

            {/* Start DateTime */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="inline h-4 w-4 mr-1" />
                Start Date & Time
              </label>
              <input
                type="datetime-local"
                value={startDateTime ?? ''}
                onChange={(e) => {
                  setStartDateTime(e.target.value);
                  // Extract date and time for backward compatibility
                  if (e.target.value) {
                    const dateOnly = e.target.value.split('T')[0];
                    const timeOnly = e.target.value.split('T')[1];
                    setBookingDate(dateOnly);
                    setStartTime(timeOnly);
                  }
                }}
                onFocus={(e) => {
                  // If empty, set to current datetime
                  if (!e.target.value) {
                    const now = new Date();
                    const minutes = Math.ceil(now.getMinutes() / 15) * 15;
                    now.setMinutes(minutes);
                    now.setSeconds(0);
                    now.setMilliseconds(0);
                    const year = now.getFullYear();
                    const month = String(now.getMonth() + 1).padStart(2, '0');
                    const day = String(now.getDate()).padStart(2, '0');
                    const hours = String(now.getHours()).padStart(2, '0');
                    const mins = String(now.getMinutes()).padStart(2, '0');
                    const currentDateTime = `${year}-${month}-${day}T${hours}:${mins}`;
                    setStartDateTime(currentDateTime);
                    setBookingDate(`${year}-${month}-${day}`);
                    setStartTime(`${hours}:${mins}`);
                  }
                }}
                min={new Date().toISOString().slice(0, 16)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
              />
            </div>

            {/* End DateTime */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date & Time
                {minEndDateTime && (
                  <span className="text-xs text-green-600 ml-2">
                    (Minimum: {new Date(minEndDateTime).toLocaleString()})
                  </span>
                )}
              </label>
              <div className="flex gap-2">
                <input
                  type="datetime-local"
                  value={endDateTime ?? ''}
                  onChange={(e) => {
                    setEndDateTime(e.target.value);
                    // Extract time for timeSlot format
                    if (e.target.value) {
                      const timeOnly = e.target.value.split('T')[1];
                      setEndTime(timeOnly);
                    }
                  }}
                  min={minEndDateTime || startDateTime || new Date().toISOString().slice(0, 16)}
                  required
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                />
                {minEndDateTime && endDateTime && endDateTime < minEndDateTime && (
                  <button
                    type="button"
                    onClick={() => setEndDateTime(minEndDateTime)}
                    className="px-3 py-2 bg-green-100 text-green-700 text-xs rounded-md hover:bg-green-200 transition-colors whitespace-nowrap"
                    title="Reset to minimum datetime"
                  >
                    Reset to Min
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                ⏱️ Minimum time calculated: Travel time + 30 min buffer. You can set a LATER time if needed, but not earlier.
              </p>
              {endDateTime && minEndDateTime && endDateTime < minEndDateTime && (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ End datetime cannot be earlier than the estimated minimum time.
                </p>
              )}
            </div>

            {/* Number of Nights */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Moon className="inline h-4 w-4 mr-1" />
                Number of Nights (Auto-calculated)
              </label>
              <input
                type="number"
                value={numberOfNights}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  setNumberOfNights(isNaN(value) ? 0 : Math.max(0, value));
                }}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 bg-white"
              />
              <p className="text-xs text-gray-500 mt-1">Automatically calculated from start and end dates. You can manually adjust if needed.</p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes (Optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                placeholder="Any special requirements or instructions..."
              />
            </div>

            {/* Estimated Price */}
            {(calculatingPrice || estimatedPrice) && (
              <div className="bg-white rounded-lg p-4">
                <h5 className="font-semibold text-gray-900 mb-2">Estimated Price</h5>
                {calculatingPrice && !estimatedPrice && (
                  <div className="flex items-center justify-center py-4">
                    <Loader className="h-5 w-5 animate-spin mr-2" />
                    <span className="text-sm text-gray-600">Calculating price...</span>
                  </div>
                )}
                {estimatedPrice && (
                <div className="space-y-1 text-sm">
                  {distance !== null && (
                    <div className="flex justify-between text-xs text-gray-600 mb-2 pb-2 border-b">
                      <span>Total Distance:</span>
                      <span className="font-semibold">{distance.toFixed(2)} km</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Distance Cost:</span>
                    <span>₹{estimatedPrice.distanceCost?.toFixed(2)}</span>
                  </div>
                  {numberOfNights > 0 && estimatedPrice.nightlyCost !== undefined && estimatedPrice.nightlyCost !== null && estimatedPrice.nightlyCost > 0 && (
                    <div className="flex justify-between">
                      <span>Nightly Cost ({numberOfNights} nights):</span>
                      <span>₹{estimatedPrice.nightlyCost.toFixed(2)}</span>
                    </div>
                  )}
                  {estimatedPrice.overtimeCharge !== undefined && estimatedPrice.overtimeCharge !== null && estimatedPrice.overtimeCharge > 0 && (
                    <div className="flex justify-between">
                      <span>Overtime Charge:</span>
                      <span>₹{estimatedPrice.overtimeCharge.toFixed(2)}</span>
                    </div>
                  )}
                  {estimatedPrice.nightDriveCharge !== undefined && estimatedPrice.nightDriveCharge !== null && estimatedPrice.nightDriveCharge > 0 && (
                    <div className="flex justify-between">
                      <span>Night Drive Charge:</span>
                      <span>₹{estimatedPrice.nightDriveCharge.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-1">
                    <span>Subtotal:</span>
                    <span>₹{estimatedPrice.subtotal?.toFixed(2)}</span>
                  </div>
                  {estimatedPrice.platformFee !== undefined && estimatedPrice.platformFee !== null && estimatedPrice.platformFee > 0 && (
                    <div className="flex justify-between">
                      <span>Platform Fee:</span>
                      <span>₹{estimatedPrice.platformFee.toFixed(2)}</span>
                    </div>
                  )}
                  {estimatedPrice.tax !== undefined && estimatedPrice.tax !== null && estimatedPrice.tax > 0 && (
                    <div className="flex justify-between">
                      <span>GST/Tax:</span>
                      <span>₹{estimatedPrice.tax.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-base border-t pt-1">
                    <span>Total:</span>
                    <span>₹{estimatedPrice.total?.toFixed(2)}</span>
                  </div>
                </div>
                )}
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Terms and Conditions Agreement */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTnC}
                  onChange={(e) => setAgreedToTnC(e.target.checked)}
                  className="mt-1 h-4 w-4 text-black focus:ring-gray-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  I agree to the{' '}
                  <a href="/terms?userType=customer" target="_blank" className="text-black hover:text-gray-800 underline font-medium">
                    Terms and Conditions
                  </a>
                  {' '}and{' '}
                  <a href="/privacy?userType=customer" target="_blank" className="text-black hover:text-gray-800 underline font-medium">
                    Privacy Policy
                  </a>
                  {' '}(Customer)
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !agreedToTnC}
              className="w-full bg-black text-white px-6 py-3 rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader className="h-5 w-5 animate-spin mr-2" />
                  Sending Request...
                </>
              ) : (
                'Send Request to All Drivers'
              )}
            </button>
          </form>
        );

      case STEPS.WAITING_FOR_DRIVER:
        return (
          <div className="text-center space-y-6 py-8">
            <div className="flex justify-center">
              <AnimatedClock size={64} color="#000000" />
            </div>
            
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Waiting for Driver to Accept</h4>
              <p className="text-gray-600">Your request has been sent to all available drivers</p>
            </div>

            {requestStatus === 'accepted' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="text-green-800 font-medium">Driver Accepted! Redirecting...</p>
              </div>
            )}

            {requestStatus === 'expired' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                <p className="text-red-800 font-medium">Request expired. Redirecting...</p>
              </div>
            )}

            {requestStatus === 'cancelled' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                <p className="text-red-800 font-medium">Request Cancelled</p>
                {cancellationReason && (
                  <p className="text-red-700 text-sm mt-2">Reason: {cancellationReason}</p>
                )}
              </div>
            )}

            {/* Cancel Button - similar to Android */}
            {requestStatus !== 'accepted' && requestStatus !== 'expired' && requestStatus !== 'cancelled' && (
              <button
                onClick={() => {
                  setShowCancellationModal(true);
                }}
                className="mx-auto flex items-center justify-center px-6 py-3 bg-red-50 border border-red-200 rounded-lg text-red-600 hover:bg-red-100 transition-colors"
              >
                <X className="h-5 w-5 mr-2" />
                Cancel Request
              </button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => {
                if (currentStep > STEPS.PICKUP) {
                  // Go to previous step
                  if (currentStep === STEPS.DROPOFF) {
                    setCurrentStep(STEPS.PICKUP);
                  } else if (currentStep === STEPS.BOOKING_DETAILS) {
                    setCurrentStep(STEPS.DROPOFF);
                  } else if (currentStep === STEPS.WAITING_FOR_DRIVER) {
                    // Can't go back from waiting screen, but can cancel
                    return;
                  }
                } else {
                  navigate('/');
                }
              }}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              {currentStep > STEPS.PICKUP && currentStep !== STEPS.WAITING_FOR_DRIVER ? 'Back' : 'Back to Home'}
            </button>
            <h2 className="text-3xl font-bold text-gray-900">Book a Driver</h2>
            <p className="text-gray-600 mt-2">Your request will be sent to all available drivers</p>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-lg shadow-md p-6">
            {renderStepContent()}

            {/* Progress Indicator */}
            {currentStep < STEPS.WAITING_FOR_DRIVER && (
              <div className="mt-6 pt-6 border-t">
                <p className="text-xs text-gray-500 font-semibold mb-2">BOOKING PROGRESS</p>
                <div className="space-y-2">
                  <div className={`text-xs flex items-center ${currentStep >= STEPS.PICKUP ? 'text-green-600' : 'text-gray-400'}`}>
                    {currentStep > STEPS.PICKUP ? '✓' : currentStep >= STEPS.PICKUP ? '→' : '○'} Pickup Location
                  </div>
                  <div className={`text-xs flex items-center ${currentStep >= STEPS.DROPOFF ? 'text-green-600' : 'text-gray-400'}`}>
                    {currentStep > STEPS.DROPOFF ? '✓' : currentStep >= STEPS.DROPOFF ? '→' : '○'} Dropoff Location
                  </div>
                  <div className={`text-xs flex items-center ${currentStep >= STEPS.BOOKING_DETAILS ? 'text-green-600' : 'text-gray-400'}`}>
                    {currentStep > STEPS.BOOKING_DETAILS ? '✓' : currentStep >= STEPS.BOOKING_DETAILS ? '→' : '○'} Booking Details
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Driver Details Modal */}
      {showDriverModal && acceptedDriver && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleCloseDriverModal}></div>
          
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <button
                onClick={handleCloseDriverModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>

              <div className="text-center">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Driver Found!</h2>
                <p className="text-gray-600 mb-6">Your booking has been confirmed</p>

                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  {acceptedDriver.avatar ? (
                    <img 
                      src={acceptedDriver.avatar} 
                      alt={acceptedDriver.name}
                      className="h-24 w-24 rounded-full object-cover mx-auto mb-4 border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="h-24 w-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                      <User className="h-12 w-12 text-black" />
                    </div>
                  )}

                  <h3 className="text-xl font-bold text-gray-900 mb-1">{acceptedDriver.name}</h3>
                  
                  <div className="flex items-center justify-center mb-3">
                    <Star className="h-5 w-5 text-yellow-400 fill-current" />
                    <span className="ml-1 text-lg font-semibold text-gray-700">
                      {acceptedDriver.rating?.toFixed(1) || '5.0'}
                    </span>
                    <span className="ml-1 text-sm text-gray-500">
                      ({acceptedDriver.reviewCount ?? 0} reviews)
                    </span>
                  </div>

                  {acceptedDriver.isVerified && (
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium mb-3">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Verified Driver
                    </div>
                  )}

                  <div className="space-y-2 text-left mt-4">
                    {acceptedDriver.phone && (
                      <div className="flex items-center text-gray-600 text-sm">
                        <Phone className="h-4 w-4 mr-2" />
                        <span>{acceptedDriver.phone}</span>
                      </div>
                    )}
                    {acceptedDriver.email && (
                      <div className="flex items-center text-gray-600 text-sm">
                        <Mail className="h-4 w-4 mr-2" />
                        <span>{acceptedDriver.email}</span>
                      </div>
                    )}
                    {acceptedDriver.experience && (
                      <div className="flex items-center text-gray-600 text-sm">
                        <Clock className="h-4 w-4 mr-2" />
                        <span>{acceptedDriver.experience} years experience</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={handleCloseDriverModal}
                    className="w-full px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors font-medium"
                  >
                    View My Booking
                  </button>
                  <p className="text-xs text-gray-500">
                    The driver will contact you shortly to confirm the details
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Reason Modal */}
      <CancellationReasonModal
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
        onConfirm={async (reason: string) => {
          try {
            if (requestId) {
              // Call API to cancel driver request with reason
              await bookingsAPI.cancelDriverRequest(requestId, reason);
            }
            // Reset request state and go back to booking details
            setRequestId(null);
            setRequestStatus('cancelled');
            setWaitingTimeLeft(null);
            setCancellationReason(reason);
            setShowCancellationModal(false);
            setCurrentStep(STEPS.BOOKING_DETAILS);
          } catch (error: any) {
            console.error('Error cancelling driver request:', error);
            setError(error.response?.data?.message || 'Failed to cancel request. Please try again.');
            setShowCancellationModal(false);
          }
        }}
      />
    </>
  );
};

export default BookDriverRequestPage;

